<template>
  <div class="document-container" style="padding-top: 0">
    <div class="document">
      <div class="header">
        <es-header>
          <template #header>
            <div class="header-title">Population Details</div>
          </template>
          <template #actions>
            <ul>
              <li class="p-narrow no-border">
                <div class="list-content">
                  <router-link
                    :to="{
                      path: '/directMatch/cep/population',
                      query: {
                        locationGroupId,
                        schoolYear,
                      },
                    }"
                    title="Go back to Location Summary"
                  >
                    <font-awesome-icon :icon="['fal', 'angle-double-left']" size="2x"/>
                  </router-link>
                </div>
              </li>
            </ul>
          </template>
        </es-header>
      </div>
      <div class="content">
        <div v-if="loading || loadingSummary" style="padding: 6.5rem">
          <es-loader/>
          <div style="padding-top: 1rem">
            Calculating population data...
          </div>
        </div>
        <div v-else class="content">
          <div class="columns flex-align--center">
            <div class="column">
              <div class="is-flex">
                <div class="is-flex flex-align--center is-size-5 has-text-weight-bold"
                  style="padding-right: 0.5rem">
                  {{ location.locationName }}
                </div>
                <div
                  class="is-flex flex-align--center has-text-grey is-italic"
                  style="padding-right: 1rem">
                  ({{ location.localCode }})
                </div>
                <div v-if="location.tags">
                  <div v-for="tag in location.tags" :key="tag.name">
                    <span class="tag is-primary">{{ tag.name }}</span>
                  </div>
                </div>
              </div>
            </div>
            <div class="column">
              <div class="is-flex flex-justify--end">
                <div
                  class="detail-item is-flex flex-justify--center is-flex flex-direction--column">
                  <div class="has-text-weight-bold is-size-5">
                    {{ location.identifiedCount && location.enrolledCount
                        ? identifiedEnrolledRatio : '--' }}
                  </div>
                  <div class="has-text-grey is-size-7">
                    Identified/Enrolled
                  </div>
                </div>
                <div class="detail-item is-flex flex-justify--center is-flex">
                  <div class="is-flex flex-align--center" style="padding-right: 1rem">
                    <es-eligibility-marker
                      v-if="location.totalISP"
                      :value="location.totalISP"
                      size="small"/>
                  </div>
                  <div class="">
                    <div class="has-text-weight-bold is-size-5">
                      {{ location.totalISP ? (location.totalISP * 100).toFixed(2) : '--' }}
                    </div>
                    <div class="has-text-grey is-size-7">
                      ISP %
                    </div>
                  </div>
                </div>
                <div
                  class="detail-item is-flex flex-justify--center is-flex flex-direction--column">
                  <div class="has-text-weight-bold is-size-5">
                    <span v-if="!location.approvalStatus">
                      --
                    </span>
                    <span
                      class="text-nowrap"
                      title="Submitted"
                      v-if="location.approvalStatus === 'S'">
                      <font-awesome-icon :icon="['fas', 'dot-circle']" class="has-text-info"/>
                      Submitted
                    </span>
                    <span
                      class="text-nowrap"
                      title="Pending"
                      v-if="location.approvalStatus === 'P'">
                      <font-awesome-icon
                        :icon="['fas', 'exclamation-circle']"
                        class="has-text-warning" />
                      Pending
                    </span>
                    <span
                      class="text-nowrap"
                      title="Accepted"
                      v-if="location.approvalStatus === 'A'">
                      <font-awesome-icon :icon="['fas', 'check-circle']" class="has-text-success" />
                      Accepted
                    </span>
                    <span
                      class="text-nowrap"
                      title="Rejected"
                      v-if="location.approvalStatus === 'R'">
                      <font-awesome-icon :icon="['fas', 'ban']" class="has-text-danger" />
                      Rejected
                    </span>
                  </div>
                  <div class="has-text-grey is-size-7">
                    Accepted Status
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="">
            <cep-details-programs
              :cepTab="cepTab"
              :location="location"
              :schoolYear="schoolYear"
              :locationGroupId="locationGroupId"
              :stateId="stateId"/>
          </div>
          <div class="student-details padding-bottom">
            <cep-details-students
              :cepTab="cepTab"
              :location="location"
              :schoolYear="schoolYear"
              :locationGroupId="locationGroupId"
              :page="pageNumIdentifiedStudents"
              :stateId="stateId"
              :sortField="sortStudentsField"
              :sortOrder="sortStudentsOrder"
              :columnHeaders="columnHeadersStudents"
              @updateSorter="updateSorter"
              @updateStudentProgramHeaders="updateStudentProgramHeaders"
            />
          </div>
        </div>
      </div>
    </div>
    <cep-legend />
  </div>
</template>

<script>
import axios from 'axios';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faAngleDoubleLeft,
} from '@fortawesome/pro-light-svg-icons';
import {
  faBan,
  faCheckCircle,
  faDotCircle,
  faExclamationCircle,
} from '@fortawesome/pro-solid-svg-icons';
import {
  mapActions,
  mapGetters,
} from 'vuex';
import CepLegend from '@/components/CepLegend.vue';
import CepDetailsPrograms from '@/components/CepDetailsPrograms.vue';
import CepDetailsStudents from '@/components/CepDetailsStudents.vue';
import EsEligibilityMarker from '@/components/EsEligibilityMarker.vue';
import FieldLabels from '@/mixins/FieldLabels';
import EsHeader from '@/components/EsHeader.vue';
import EsLoader from '@/components/EsLoader.vue';

library.add(
  faAngleDoubleLeft,
  faBan,
  faCheckCircle,
  faDotCircle,
  faExclamationCircle,
);

export default {
  components: {
    CepLegend,
    CepDetailsPrograms,
    CepDetailsStudents,
    EsEligibilityMarker,
    EsHeader,
    EsLoader,
  },
  mixins: [
    FieldLabels,
  ],
  props: {
    cepSummary: {
      type: Object,
    },
    cepTab: {
      type: String,
    },
    loadingSummary: {
      type: Boolean,
    },
    loadingSummaryError: {
      type: Boolean,
    },
    locationGroupId: {
      type: [String, Number],
    },
    schoolYear: {
      type: Number,
    },
    stateId: {
      type: Number,
    },
    sortStudentsField: {
      type: String,
      default: 'stateStudentId',
    },
    sortStudentsOrder: {
      type: String,
      default: 'ASC',
    },
  },
  data() {
    return {
      loading: true,
      loadingError: null,
      location: {},
      columnHeadersStudents: [],
      pageNumIdentifiedStudents: 1,
    };
  },
  async created() {
    await this.loadPersonTypes();
    this.getLocationSummary();
    this.columnHeadersStudents = [
      {
        header: `${this.getFieldLabel(`PER:${this.defaultPersonType}:DIRECTMATCH:SCHOOL_CODE`)} Code`,
        field: 'schoolCode',
        order: null,
        sortable: true,
      },
      {
        header: 'Student ID',
        field: 'stateStudentId',
        order: 'ASC',
        sortable: true,
      },
      {
        header: 'Count',
        field: 'programCount',
        order: null,
        sortable: true,
      },
    ];
  },
  computed: {
    ...mapGetters('lookups', [
      'defaultPersonType',
    ]),
    identifiedEnrolledRatio() {
      return `${this.location.identifiedCount} / ${this.location.enrolledCount}`;
    },
    getStudentsSorter() {
      return this.columnHeadersStudents.find(header => header.order);
    },
    searchParams() {
      const params = {};

      if (this.stateId) {
        params.stateId = this.stateId;
      }
      if (this.locationGroupId) {
        params.locationGroupId = this.locationGroupId;
      }
      if (this.schoolYear) {
        params.schoolYear = this.schoolYear;
      }
      if (this.getStudentsSorter) {
        params.sortStudentsField = this.getStudentsSorter.field;
        params.sortStudentsOrder = this.getStudentsSorter.order;
      }
      return params;
    },
  },
  methods: {
    ...mapActions('lookups', [
      'loadPersonTypes',
    ]),
    async getLocationSummary() {
      try {
        this.loading = true;
        this.loadingError = false;

        const res = await axios.get(`cep/${this.locationGroupId}/population/${this.schoolYear}/location-summary?schoolId=${this.stateId}`);
        this.location = res.data;
      } catch (e) {
        this.loadingError = true;
      } finally {
        this.loading = false;
      }
    },
    updateSorter() {
      // reset pageNum back to 1 when sorting.
      this.pageNumIdentifiedStudents = 1;
      this.$router.replace({ query: this.searchParams });
    },
    updateStudentProgramHeaders(programs) {
      this.columnHeadersStudents = [
        {
          header: `${this.getFieldLabel(`PER:${this.defaultPersonType}:DIRECTMATCH:SCHOOL_CODE`)} Code`,
          field: 'schoolCode',
          order: null,
          sortable: true,
        },
        {
          header: 'Student ID',
          field: 'stateStudentId',
          order: null,
          sortable: true,
        },
      ];
      programs.forEach((program, i) => this.columnHeadersStudents.push(
      {
        header: program,
        field: null,
        order: null,
        sortable: false,
      },
    ));
    this.columnHeadersStudents.push(
      {
        header: 'Count',
        field: 'programCount',
        order: null,
        sortable: true,
      });
    },
  },
};
</script>

<style lang="sass" scoped>
  .detail-item
    padding: 0.5rem 1rem
    min-width: 165px
    border-left: 1px solid #ccc
</style>
